import { useCallback, useEffect } from 'react';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { useLocation, useHistory } from 'react-router-dom';

import useLanguage from 'container/Mainlayout/useLanguage';
import i18n, { changeStoreLanguage, detectLanguage } from 'i18n';
import {
  getAccessToken,
  getLocalStorage,
  removeAccessToken,
  removeLocalStorage,
} from 'services/common/storage';
import { LanguageKey } from 'services/systems/types';
import { getIndexQuotesSocketAction } from 'store/AnalyticsCenter';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getMenusAction } from 'store/menus';
import {
  getPageStaticAllAction,
  getSystemDataAction,
  setInitializedGA,
  setLanguage,
  toggleIsChangeLanguage,
  toggleLoading,
} from 'store/system';
import { getProfileAction, logoutAction } from 'store/user';
import { LOCAL_STORAGE } from 'utils/constants';
import {
  checkActiveLang,
  getActiveLanguages,
  getLangURL,
} from 'utils/language';

const useInitializeRender = () => {
  const isLogoutSSO = getLocalStorage(LOCAL_STORAGE.IS_LOGOUT_SSO);
  const location = useLocation();
  const { push } = useHistory();
  const dispatch = useAppDispatch();

  const isChangeLanguage = useAppSelector(
    (state) => state.system.isChangeLanguage,
  );
  const system = useAppSelector((state) => state.system.dataSystem);

  const { handleShowModal } = useLanguage();

  const initialData = useCallback(async () => {
    try {
      dispatch(toggleLoading(true));
      dispatch(getPageStaticAllAction());
      dispatch(getIndexQuotesSocketAction());
      dispatch(getMenusAction());
      await dispatch(getSystemDataAction())
        .unwrap()
        .then(async (dataSystem) => {
          const lang = detectLanguage() as LanguageKey;

          if (!checkActiveLang(lang, dataSystem?.locales)) {
            const message = dataSystem?.locales
              ? dataSystem.locales[lang].message
              : '';
            handleShowModal(lang, message, () => {
              const firstLangKeyActive = getActiveLanguages(
                dataSystem?.locales,
              )[0];
              if (firstLangKeyActive) {
                dispatch(setLanguage(firstLangKeyActive));
                i18n.changeLanguage(firstLangKeyActive, () => {
                  push(`${getLangURL(firstLangKeyActive)}`);
                });
              }
            });
          }
        });
      if (getAccessToken()) {
        await dispatch(getProfileAction()).unwrap();
      }
    // TODO: adding actions
    } catch {
      // empty
    } finally {
      dispatch(toggleLoading(false));
    }
  }, [handleShowModal, dispatch, push]);

  useEffect(() => {
    initialData();
  }, [initialData]);

  //* Load new system data when language change
  const loadData = useCallback(() => {
    dispatch(getSystemDataAction());
    dispatch(getMenusAction());
    dispatch(toggleIsChangeLanguage(false));
    dispatch(getPageStaticAllAction());
  }, [dispatch]);

  useEffect(() => {
    if (isChangeLanguage) {
      loadData();
    }
  }, [isChangeLanguage, loadData]);
  //* End load new system

  //* GTM-GA
  useEffect(() => {
    if (system?.gaId) {
      ReactGA.initialize(system.gaId);
      dispatch(setInitializedGA(true));
    }
    if (system?.gtmId) {
      TagManager.initialize({
        gtmId: system.gtmId,
      });
    }
  }, [system, dispatch]);
  //* End GTM-GA

  //* Handle back, forward button
  const history = useHistory();
  useEffect(() => {
    const back = history.listen((nextLocation) => {
      if (history.action === 'POP') {
        const checkLanguage = changeStoreLanguage(nextLocation.pathname);
        if (checkLanguage.change) {
          i18n.changeLanguage(checkLanguage.language);
          dispatch(setLanguage(checkLanguage.language));
        }
      }
    });
    return back;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //* End back, forward button

  useEffect(() => {
    document.body.classList.remove('overflow-body');
    document.documentElement.classList.remove('overflow-body');
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (isLogoutSSO) {
      dispatch(logoutAction());
      removeAccessToken();
      removeLocalStorage(LOCAL_STORAGE.IS_LOGOUT_SSO);
    }
  }, [dispatch, isLogoutSSO]);
};

export default useInitializeRender;
